import {
  FccDoorActuationError,
  FccTargetDoorStatus,
} from '@backoffice-frontend/graphql/subscription'
import type { Theme } from '@backoffice-frontend/patterns'
import { MoiaButton, VerticalDivider } from '@backoffice-frontend/patterns'
import {
  CheckCircleSolidIcon,
  WarningSolidIcon,
  WarningXSolidIcon,
} from '@moia-dev/pace-icons'
import { Heading } from '@moia-dev/pace-web'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { DoorIcon } from './DoorIcon'

type DoorStateCheckProps = {
  errors: FccDoorActuationError[]
  currentDoorStatus?: FccTargetDoorStatus
  pendingDoorActuation?: FccTargetDoorStatus
  handleDoorActuation: () => void
  mutationLoading: boolean
}

export const DoorStateCheck = ({
  errors,
  currentDoorStatus,
  pendingDoorActuation,
  handleDoorActuation,
  mutationLoading,
}: DoorStateCheckProps) => {
  const { t } = useTranslation()

  const doorRequestStatus = getDoorRequestStatus({
    currentDoorStatus,
    mutationLoading,
    errors,
    pendingDoorActuation,
  })

  return (
    <div
      css={theme => ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2),
        background: getBackgroundColor(doorRequestStatus, theme),
        border: `1px solid ${getBorderColor(doorRequestStatus, theme)}`,
        borderRadius: 8,
        gap: theme.spacing(1),
      })}
    >
      <div
        css={theme => ({
          display: 'grid ',
          alignItems: 'center',
          columnGap: theme.space.Space3,
          gridTemplateColumns: '24px 1fr',
          color: theme.semantic.ColorContentDefault,
        })}
      >
        <DoorIcon
          css={theme => ({
            marginRight: theme.space.Space3,
            color: getContentColor(doorRequestStatus, theme),
          })}
        />
        <div>
          <Heading
            size="md"
            css={theme => ({
              color: getContentColor(doorRequestStatus, theme),
            })}
          >
            {getTitleText({
              currentDoorStatus,
              pendingDoorActuation,
              hasErrors: errors.length > 0,
              t,
            })}
          </Heading>
          {errors.length > 0 && (
            <Heading
              size="md"
              css={theme => ({
                fontWeight: 'normal',
                color: getContentColor(doorRequestStatus, theme),
              })}
            >
              {getSubtitleText(errors, t, pendingDoorActuation)}
            </Heading>
          )}
        </div>
      </div>
      <div
        css={theme => ({
          height: 60,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(2),
        })}
      >
        <MoiaButton
          color="secondary"
          onClick={handleDoorActuation}
          submitting={doorRequestStatus === 'loading'}
        >
          {currentDoorStatus === FccTargetDoorStatus.Open
            ? t('adFleetMap_closeDoor_button')
            : t('map_openDoor_button')}
        </MoiaButton>
        <VerticalDivider
          css={theme => ({
            borderColor: getBorderColor(doorRequestStatus, theme),
          })}
        />
        <StatusIcon status={doorRequestStatus} />
      </div>
    </div>
  )
}

type StatusIconProps = {
  status: 'success' | 'loading' | 'waiting' | 'error' | 'warning'
}

const StatusIcon = ({ status }: StatusIconProps) => {
  switch (status) {
    case 'success':
      return (
        <CheckCircleSolidIcon
          css={theme => ({ color: theme.semantic.ColorSuccessContrast })}
        />
      )
    case 'loading':
    case 'waiting':
      return (
        <div
          css={theme => ({
            width: 20,
            height: 20,
            background: theme.semantic.ColorSurfaceReduced,
            borderRadius: '100%',
          })}
        />
      )
    case 'error':
      return (
        <WarningXSolidIcon
          css={theme => ({ color: getContentColor('error', theme) })}
        />
      )
    case 'warning':
      return (
        <WarningSolidIcon
          css={theme => ({ color: getContentColor('warning', theme) })}
        />
      )
  }
}

const getTitleText = ({
  currentDoorStatus,
  pendingDoorActuation,
  hasErrors,
  t,
}: {
  currentDoorStatus?: FccTargetDoorStatus
  pendingDoorActuation: FccTargetDoorStatus | undefined
  hasErrors: boolean
  t: TFunction
}) => {
  if (currentDoorStatus === undefined) {
    return t('adFleetMap_tag_vehicleVizDoorStateUnavailable_label')
  }
  if (pendingDoorActuation === FccTargetDoorStatus.Closed && !hasErrors) {
    return t('common_closing_label')
  }
  if (pendingDoorActuation === FccTargetDoorStatus.Open && !hasErrors) {
    return t('common_opening_label')
  }
  if (currentDoorStatus === FccTargetDoorStatus.Open) {
    return t('adFleetMap_doorOpen_label')
  }
  return t('adFleetMap_doorClosed_label')
}

const getSubtitleText = (
  errors: FccDoorActuationError[],
  t: TFunction,
  pendingDoorActuation?: FccTargetDoorStatus,
) => {
  for (const error of errors) {
    if (error === FccDoorActuationError.VehicleMoving) {
      if (pendingDoorActuation === FccTargetDoorStatus.Closed) {
        return t(
          'adFleetMap_vehicleVizDoorCouldNotBeClosedVehicleMoving_caption',
        )
      }
      return t('adFleetMap_vehicleVizDoorCouldNotBeOpenedVehicleMoving_caption')
    }
  }

  return t('adFleetMap_vehicleVizDoorNotFunctional_label')
}

const getDoorRequestStatus = ({
  currentDoorStatus,
  mutationLoading,
  errors,
  pendingDoorActuation,
}: {
  currentDoorStatus?: FccTargetDoorStatus
  mutationLoading: boolean
  errors: FccDoorActuationError[]
  pendingDoorActuation?: FccTargetDoorStatus
}) => {
  const isDoorTelemetryMissing = currentDoorStatus === undefined

  if (errors.length > 0) {
    if (
      pendingDoorActuation === FccTargetDoorStatus.Open ||
      currentDoorStatus === FccTargetDoorStatus.Closed
    ) {
      return 'warning'
    }
    return 'error'
  }
  if (mutationLoading || pendingDoorActuation !== undefined) {
    return 'loading'
  }
  if (isDoorTelemetryMissing) {
    return 'error'
  }
  if (currentDoorStatus === FccTargetDoorStatus.Open) {
    return 'waiting'
  }
  return 'success'
}

const getBackgroundColor = (actuationState: string, theme: Theme) => {
  if (actuationState === 'success') {
    return theme.semantic.ColorSurfaceSuccess
  }
  if (actuationState === 'error') {
    return theme.semantic.ColorCriticalAlternative
  }
  if (actuationState === 'warning') {
    return theme.semantic.ColorWarningAlternative
  }
  return theme.semantic.ColorSurfaceReducedAlternative
}

const getContentColor = (actuationState: string, theme: Theme) => {
  if (actuationState === 'success') {
    return theme.semantic.ColorSuccessContrast
  }
  if (actuationState === 'error') {
    return theme.semantic.ColorCriticalContrast
  }
  if (actuationState === 'warning') {
    return theme.semantic.ColorWarningContrast
  }
  return theme.semantic.ColorContentDefault
}

const getBorderColor = (actuationState: string, theme: Theme) => {
  if (actuationState === 'success') {
    return theme.semantic.ColorSuccessContrast
  }
  if (actuationState === 'error') {
    return theme.semantic.ColorCriticalContrast
  }
  if (actuationState === 'warning') {
    return theme.semantic.ColorWarningContrast
  }
  return theme.semantic.ColorAccessoryDefault
}
